/* eslint no-underscore-dangle: 0 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toPng } from 'html-to-image';
import { setErrorNotification } from '../../../../redux/slices/NotificationSlice';
import ModalBox from '../../../../shared/component/ModalBox';
import TemplateContentUpload from '../TemplateContentUpload';
import HtmlHeader from '../HtmlHeader';
import utils from '../templateFunctions';
import FileFormat from '../FileFormat';
import { createOrUpdateTemplate } from '../../../../redux/slices/TemplateSlice';
import PopupBox from '../ContentUploadConfirmation';

function HtmlTemplateNineteen({ templateData }) {
  const [data, setData] = useState([]);
  const [notification, setNotification] = useState(false);
  const [openContentUploadConfirmation, setOpenContentUploadConfirmation] = useState(false);
  const [base64, setBase64] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [disable, setDisable] = useState(true);
  const [modalView, setModalView] = useState({ content: '', title: '' });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const htmlRef = useRef(null);
  const convertBase64Ref = useRef(null);
  const convertHtmlRef = useRef(null);

  const cssStyles = `
    body {
      margin: 0;
      padding: 0;
    }
    .fashion-card {
      position: relative;
      font-family: "Montserrat", sans-serif;
      box-sizing: border-box;
    }
    .fashion-card__content {
      height: 87vh;
      max-width: max-content;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
    @media (max-width: 768px) and (orientation: portrait) {
      .fashion-card__content {
        height: auto;
        min-height: 100vh;
        padding: 20px;
        justify-content: flex-start;
      }
    }
    .fashion-card .main-container {
      padding: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
    }
    @media (max-width: 768px) and (orientation: portrait) {
      .fashion-card .main-container {
        padding: 20px 15px;
      }
    }
    .fashion-card__image {
      overflow: hidden;
      height: 100%;
      margin-block-start: 70px;
    }
    @media (max-width: 768px) and (orientation: portrait) {
      .fashion-card__image {
        margin-block-start: 40px;
      }
    }
    .fashion-card__image img {
      border-radius: 100% 100% 0 0;
      width: 400px;
      height: 400px;
      object-fit: cover;
    }
    @media (max-width: 768px) {
      .fashion-card__image img {
        width: 300px;
        height: 300px;
      }
    }
    @media (max-width: 480px) {
      .fashion-card__image img {
        width: 250px;
        height: 250px;
      }
    }
    .fashion-card__text {
      color: #3e3e3e;
      font-size: 16px;
      position: absolute;
      font-weight: 600;
    }
    @media (max-width: 768px) {
      .fashion-card__text {
        position: relative;
        text-align: center;
        margin: 10px 0;
      }
    }
    .fashion-card__top-right {
      top: 80px;
      inset-inline-end: 15%;
      font-size: 21px;
    }
    @media (max-width: 768px) {
      .fashion-card__top-right {
        position: relative;
        top: auto;
        inset-inline-end: auto;
        order: 1;
      }
    }
    .fashion-card__bottom-left {
      width: 130px;
      inset-inline-start: 35%;
      bottom: 20%;
      font-size: 18px;
    }
    @media (max-width: 768px) {
      .fashion-card__bottom-left {
        position: relative;
        width: auto;
        inset-inline-start: auto;
        bottom: auto;
        order: 3;
      }
    }
    .fashion-card__bottom-right {
      bottom: 20%;
      right: 15%;
      font-size: 21px;
    }
    @media (max-width: 768px) {
      .fashion-card__bottom-right {
        position: relative;
        bottom: auto;
        right: auto;
        order: 4;
      }
    }
    .fashion-card__vertical-text {
      position: absolute;
      top: 10%;
      inset-inline-end: 15%;
      width: 20%;
      transform: rotate(-90deg) translateY(-50%);
      transform-origin: right center;
      font-size: 45px;
      font-weight: bold;
      color: #3e3e3e;
    }
    @media (max-width: 768px) {
      .fashion-card__vertical-text {
        position: relative;
        transform: none;
        width: auto;
        top: auto;
        inset-inline-end: auto;
        order: 2;
        font-size: 32px;
        margin: 20px 0;
      }
    }
    @media (max-width: 480px) {
      .fashion-card__vertical-text {
        font-size: 28px;
      }
    }
    @media (max-width: 768px) {
      .fashion-card__content {
        gap: 15px;
      }
    }
  `;

  const convertImage = async (url) => {
    await utils.convertSignedUrl(url)
      .then((response) => {
        setDisable(false);
        dispatch(createOrUpdateTemplate(response));
      })
      .catch((error) => {
        setDisable(false);
        dispatch(setErrorNotification(error?.response?.data));
      });
  };

  // const convertBase64 = async () => {
  //   try {
  //     const dataUrl = await toPng(htmlRef.current, { cacheBust: true });
  //     setBase64(dataUrl);
  //     convertImage(dataUrl);
  //   } catch (error) {
  //     dispatch(setErrorNotification(error?.response?.data));
  //   }
  // };

  convertBase64Ref.current = async () => {
    try {
      const dataUrl = await toPng(htmlRef.current, { cacheBust: true });
      setBase64(dataUrl);
      convertImage(dataUrl);
    } catch (error) {
      dispatch(setErrorNotification(error?.response?.data));
    }
  };

  convertHtmlRef.current = () => {
    if (htmlRef.current) {
      const content = htmlRef.current.outerHTML;
      const fullHtml = `
        <html>
          <head>
              <link rel="preconnect" href="https://fonts.googleapis.com" />
              <link rel="preconnect" href="https://fonts.gstatic.com" />
              <link 
              href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Urbanist:wght@100..900&display=swap" 
              rel="stylesheet" />
              <style>${cssStyles}</style>
          </head>
          <body>${content}</body>
        </html>
      `;
      setHtmlContent(fullHtml); // Set the HTML content as a string
    }
  };

  useEffect(() => {
    utils?.initializer(templateData).then((res) => (setData(res)));
    utils?.reload();
  }, [templateData]);

  /* useEffect runs convertBase64 after data is updated */
  useEffect(() => {
    if (data) {
      setTimeout(() => {
        convertBase64Ref.current?.();
      }, 1000);
      convertHtmlRef.current?.();
    }
  }, [data]);

  const handleFileType = (fileType) => {
    setModalView({
      title: 'content',
      content: <TemplateContentUpload
        base64={base64}
        setNotification={setNotification}
        fileType={fileType}
        htmlContent={htmlContent}
      />,
    });
  };

  const onClickContentUpload = () => {
    setNotification(true);
    setModalView({
      title: t('fileFormat'),
      content: <FileFormat
        setNotification={setNotification}
        handleFileType={handleFileType}
        setOpenContentUploadConfirmation={setOpenContentUploadConfirmation}
        data={data}
      />,
    });
  };

  const contentDetails = data?.[0]?.template_contents?.items?.[0]?.details;
  const backgroundColor = data?.[0]?.styles?.background;

  return (
    <div>
      <HtmlHeader
        htmlData={data}
        disable={disable}
        click={onClickContentUpload}
      />

      <ModalBox
        status={notification}
        closeModal={setNotification}
        modalView={modalView}
      />

      <ModalBox
        status={openContentUploadConfirmation}
        closeModal={setOpenContentUploadConfirmation}
        modalView={{
          title: 'Confirmation',
          content: <PopupBox
            setOpenContentUploadConfirmation={setOpenContentUploadConfirmation}
            setNotification={setNotification}
          />,
        }}
      />

      <div className="header-height" />

      <div ref={htmlRef} className="fashion-card">

        <div style={{ backgroundColor: `${backgroundColor}` }} className="main-container">
          <div className="fashion-card__content">
            <p className="fashion-card__text fashion-card__top-right">{contentDetails?.[0]?.header}</p>
            <div className="fashion-card__image">
              <img src={contentDetails?.[0]?.image} alt="Fashion" />
            </div>
            <p className="fashion-card__text fashion-card__bottom-left">{contentDetails?.[0]?.description}</p>
            <p className="fashion-card__text fashion-card__bottom-right">{contentDetails?.[0]?.website}</p>
            <p className="fashion-card__vertical-text">{contentDetails?.[0]?.name}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

HtmlTemplateNineteen.propTypes = {
  templateData:
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      template_title: PropTypes.string.isRequired,
    }).isRequired,
  // Ensure templateData is an array of objects with specific shape
};

export default HtmlTemplateNineteen;
